import React, { type SyntheticEvent, type FC, type PropsWithChildren, useContext } from 'react';

import { withImage, useSmartlingStrings } from '@lucid/hooks';

import reactHTMLParser from '../../utils/reactHTMLParser';
import Box from '../box/box';
import Heading from '../heading/heading';
import Link from '../link/link';
import type { Lucid } from '../../types/types';
import Image from '../image/image';
import { Container, LayoutContext } from '..';

const STICKY_NAV_HEIGHT = 64;

export interface FeatureListProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** List of features to display. */
  list: Lucid.Directus.Generated.Paper_Block[];
}

/**
 * scrollIntoView()
 * When a Table of Contents item is clicked, scroll to the section in the article
 * */
const scrollIntoView = (evt: SyntheticEvent<HTMLAnchorElement>, id: string) => {
  evt.preventDefault();
  const element = document.querySelector(`#${id}`) as HTMLElement;

  if (element) {
    if (!element.hasAttribute('tabindex')) {
      element.setAttribute('tabindex', '-1');
    }
    element.focus({ preventScroll: true });

    const topOffset = element.getBoundingClientRect().top + window.scrollY - STICKY_NAV_HEIGHT - 16;

    window.scrollTo({
      top: topOffset,
      behavior: 'smooth',
    });
  }
};

const FeatureList: FC<PropsWithChildren<FeatureListProps>> = ({ className, list, ...rest }) => {
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_sunrise_toc'],
    shortCode,
  });

  return (
    <Container>
      <Box
        {...rest}
        as="section"
        css={theme => ({
          label: 'featureList',
          display: 'grid',
          [theme.breakpoints.bp768.min]: {
            padding: `${theme.space.s80}px 0`,
            gap: theme.space.s80,
            gridTemplateColumns: '280px 1fr',
          },
        })}
      >
        <Box css={theme => ({ position: 'relative', marginTop: theme.space.s64 })}>
          <Box
            css={theme => ({
              border: `1px solid ${theme.colors.coolGrey[20]}`,
              padding: theme.space.s32,
              position: 'sticky',
              top: theme.space.s104,
            })}
          >
            <Heading as="h2" css={theme => ({ marginBottom: theme.space.s32 })} variant="headingM">
              {withSmartling('mktg_sunrise_toc')}
            </Heading>
            {list.map((item, index) => (
              <Link
                key={`feature-list-link-${index}`}
                css={theme => ({
                  display: 'block',
                  marginBottom: theme.space.s24,
                })}
                data-testid={`feature-list-link-${index}`}
                href={`#section-${index}`}
                onClick={evt => scrollIntoView(evt, `section-${index}`)}
                onKeyDown={evt => {
                  if (['Enter'].includes(evt.code)) {
                    scrollIntoView(evt, `section-${index}`);
                  }
                }}
                variant="secondaryAlternate"
              >
                {item.heading}
              </Link>
            ))}
          </Box>
        </Box>
        <Box className={className}>
          {list.map((item, index) => (
            <Box
              key={`feature-list-${index}`}
              css={theme => ({
                borderBottom: `1px solid ${theme.colors.coolGrey[20]}`,
                '&:last-of-type': {
                  borderBottom: 0,
                },
                padding: `${theme.space.s64}px 0`,
              })}
            >
              <Box
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  maxWidth: '580px',
                }}
              >
                <Heading as="h2" id={`section-${index}`} variant="headingL">
                  {item.heading}
                </Heading>
                {item.html && (
                  <Box
                    key={`FeatureListKey-${index}`}
                    css={theme => [theme.shared.htmlParserStyles, { a: { display: 'inline' } }]}
                  >
                    {reactHTMLParser(item.html)}
                  </Box>
                )}
                {item.link && (
                  <Link href={item.link.final_url || ''} iconAnimation="navigational" variant="secondary">
                    {item.link.default_link_text}
                  </Link>
                )}
                {item.image && (
                  <Image
                    css={theme => ({ marginTop: theme.space.s64 })}
                    dataElementPageLocation="hero"
                    imageAlt=""
                    imageSrc={withImage(item.image)}
                  />
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Container>
  );
};

export { FeatureList };
export default FeatureList;
